import React, { useState, useEffect } from 'react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';

// lodash not required here
import ListResource from './ListResource.jsx';
import BtnToggle from '~/commons/components/BtnToggle.jsx';
import { env } from '../../config';
import { getLang } from '~/commons/helpers';
import useHttpStatus from '~/commons/hooks/useHttpStatus';

// Utility: get assignment level from grade
function getAssignmentLv(grade) {
  return [1, 2, 3].includes(Number(grade)) ? 'p1' : 'p2';
}

const isAdmin = env.user.roles.includes('admin');

const ShowStudentRankingSwitch = () => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(null);
  const [res, req] = useHttpStatus();

  useEffect(() => {
    req({ url: '/api/core/schoolclasses/showRankings' });
  }, []);

  useEffect(() => {
    if (res.status === 'success') {
      setChecked(res.payload === true);
    }
  }, [res.status, res.payload]);

  if (checked === null) {
    return null;
  }
  return (
    <div className="inline-block float-right">
      <div className="form-group">
        <label className="pr-3" htmlFor="showSchoolClassRankings">{t('Show Student Page Class Rankings')}</label>
        <div className="d-inline-block custom-control custom-switch">
          <input
            className="custom-control-input"
            type="checkbox"
            role="switch"
            id="showSchoolClassRankings"
            checked={checked}
            onChange={(evt) => {
              // console.log('onChannge', evt.target.checked)
              // setChecked(evt.target.checked);
              req({
                url: '/api/core/schoolclasses/updateShowRankings',
                method: 'patch',
                data: { hideRanking: !evt.target.checked },
              });
            }}
          />
          <label className="custom-control-label" htmlFor="showSchoolClassRankings"></label>
        </div>
      </div>
    </div>
  );
};

let withins = [
  // { key: 'school', label: 'School' },
  { key: 'all', label: 'Hong Kong' },
];

if (!isAdmin) {
  withins = withins.filter((w) => w.key !== 'district');
}

const api = '/api/quiz/rankings';

const ListRankings = () => {
  const stage = env?.school?.stage;
  const [period, setPeriod] = useState('');
  const [group, setGroup] = useState({
    unitType: 'student',
    within: 'all',
  });
  const [district, setDistrict] = useState('');
  const { unitType, within } = group;
  const { t, i18n: { language } } = useTranslation();
  const lang = language === 'en' ? 'en' : 'zhhk';
  // period state used to control displayed columns
  const filter = {};
  if (district) filter.district = district;
  if (period) filter.period = period;

  // build searchable options depending on selected unitType
  let searchableOptions = [];
  if (unitType === 'schoolClass') {
    searchableOptions = [
      { value: 'unit.title', label: 'Class' },
      { value: 'unit.grade', label: 'Grade', options: Object.keys(env.grades).map((g) => ({ value: g, label: env.grades[g][lang] })) },
    ];
  } else if (unitType === 'school') {
    searchableOptions = [
      { value: 'unit.title', label: 'School' },
      {
        value: 'unit.district',
        label: 'District',
        options: Object.keys(env.districts).map((d) => (
          { value: d, label: env.districts[d][lang] }
        )),
      },
    ];
  } else {
    searchableOptions = [
      { value: 'unit.name', label: 'Name' },
      { value: 'unit.schoolClass.title', label: 'Class' },
      { value: 'unit.schoolClass.grade', label: 'Grade', options: Object.keys(env.grades).filter((g) => { if (stage === 'p') return g <= 6; if (stage === 's') return g >= 7; return true; }).map((g) => ({ value: g, label: env.grades[g][lang] })) },
      { value: 'unit.studentId', label: 'Class No.' },
    ];
  }

  // columns specific to the selected unitType
  let unitCols = [];
  if (unitType === 'student') {
    const periodCols = (p) => {
      const timeGetter = (v) => {
        if (!v || v === 0) return '';
        const minutes = Math.floor(v / 60);
        const seconds = v % 60;
        return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
      };

      if (!p) {
        return [
          {
            key: 'period1TimeSpent',
            children: 'P1 TimeSpent',
            sortable: false,
            getter: timeGetter,
          },
          {
            key: 'period1Score',
            children: 'P1 Score',
            sortable: false,
            getter: (v) => v || 0,
          },
          {
            key: 'period1Rank',
            children: 'P1 Rank',
            sortable: false,
            getter: (v, r) => {
              const d = r && r.period1RankDense;
              if (Number.isFinite(d)) return d;
              if (Number.isFinite(v)) return v;
              return '';
            },
          },

          {
            key: 'period2TimeSpent',
            children: 'P2 TimeSpent',
            sortable: false,
            getter: timeGetter,
          },
          {
            key: 'period2Score',
            children: 'P2 Score',
            sortable: false,
            getter: (v) => v || 0,
          },
          {
            key: 'period2Rank',
            children: 'P2 Rank',
            sortable: false,
            getter: (v, r) => {
              const d = r && r.period2RankDense;
              if (Number.isFinite(d)) return d;
              if (Number.isFinite(v)) return v;
              return '';
            },
          },

          {
            key: 'period3TimeSpent',
            children: 'P3 TimeSpent',
            sortable: false,
            getter: timeGetter,
          },
          {
            key: 'period3Score',
            children: 'P3 Score',
            sortable: false,
            getter: (v) => v || 0,
          },
          {
            key: 'period3Rank',
            children: 'P3 Rank',
            sortable: false,
            getter: (v, r) => {
              const d = r && r.period3RankDense;
              if (Number.isFinite(d)) return d;
              if (Number.isFinite(v)) return v;
              return '';
            },
          },
        ];
      }

      // only show selected period columns (allow sorting)
      return [
        // {
        //   key: `period${p}Finished`,
        //   children: `P${p} Finished`,
        //   sortable: false,
        //   getter: (v) => v || 0,
        // },
        {
          key: `period${p}Score`,
          children: `P${p} Score`,
          sortable: true,
          getter: (v) => v || 0,
        },
        {
          key: `period${p}TimeSpent`,
          children: `P${p} TimeSpent`,
          sortable: false,
          getter: timeGetter,
        },
        {
          key: `period${p}Rank`,
          children: `P${p} Rank`,
          sortable: true,
          getter: (v, r) => {
            const d = r && r[`period${p}RankDense`];
            if (Number.isFinite(d)) return d;
            if (Number.isFinite(v)) return v;
            return '';
          },
        },
      ];
    };

    unitCols = [
      {
        key: 'unit.name.full.en',
        children: 'Name',
        sortable: true,
        getter: (v, r) => getLang(get(r, 'unit.name.full'), lang),
      },
      {
        key: 'unit.schoolClass.title',
        children: 'Class',
        sortable: true,
      },
      {
        key: 'unit.school.title',
        children: 'School',
        sortable: true,
        getter: (v, r) => getLang(get(r, 'unit.school.title'), lang),
      },
      {
        key: 'unit.school.district',
        children: 'School district',
        sortable: true,
        getter: (v) => {
          if (!v) return '';
          return env.districts[v][lang];
        },
      },
      {
        key: 'unit.schoolClass.grade',
        children: 'Group',
        sortable: true,
        getter: (v, r) => {
          // Use assignment level from grade if available
          const grade = r?.unit?.schoolClass?.grade;
          if (!grade) return '';
          const lv = getAssignmentLv(grade);
          return env.lvs[lv]?.[lang] || lv;
        },
      },
      {
        key: 'unit.studentNo',
        children: 'Class No.',
        sortable: true,
        getter: (v, r) => r?.unit?.studentId,
      },
      ...periodCols(period),
    ];
  } else if (unitType === 'school') {
    const periodColsSchool = (p) => {
      if (!p) {
        return [
          // {
          //   key: 'period1Finished',
          //   children: 'P1 Finished',
          //   sortable: false,
          //   getter: (v) => v || 0,
          // },
          {
            key: 'period1Score',
            sortable: true,
            getter: (v) => v || 0,
          },
          // {
          //   key: 'period2Finished',
          //   children: 'P2 Finished',
          //   sortable: false,
          //   getter: (v) => v || 0,
          // },
          {
            key: 'period2Score',
            children: 'P2 Score',
            sortable: false,
            getter: (v) => v || 0,
          },
          // {
          //   key: 'period3Finished',
          //   children: 'P3 Finished',
          //   sortable: false,
          //   getter: (v) => v || 0,
          // },
          {
            key: 'period3Score',
            children: 'P3 Score',
            sortable: false,
            getter: (v) => v || 0,
          },
        ];
      }

      return [
        // {
        //   key: `period${p}Finished`,
        //   children: `P${p} Finished`,
        //   sortable: false,
        //   getter: (v) => v || 0,
        // },
        {
          key: `period${p}Score`,
          children: `P${p} Score`,
          // allow sorting by the selected period's score for schools
          sortable: true,
          getter: (v) => v || 0,
        },
      ];
    };

    unitCols = [
      {
        key: 'unit.title',
        children: 'School',
        sortable: true,
        getter: (v, r) => getLang(get(r, 'unit.title'), lang),
      },
      {
        key: 'unit.district',
        children: 'School district',
        sortable: false,
        getter: (v) => {
          if (!v) return '';
          return env.districts[v][lang];
        },
      },
      ...periodColsSchool(period),
    ];
  } else {
    unitCols = [
      {
        key: 'unit.title',
        children: 'Class',
        sortable: true,
      },
    ];
  }

  // compute default sort object (avoid nested ternaries in JSX)
  let defaultSort;
  if (period) {
    const key = `period${period}Score`;
    defaultSort = {};
    defaultSort[key] = -1;
  } else if (unitType === 'school') {
    defaultSort = { score: 1 };
  } else {
    // For "All Periods" student view, sort by total score descending
    defaultSort = { score: -1 };
  }

  return (
    <>
      <div className="form-row my-3">
        <div className="col">
          <BtnToggle
            className="btn-block btn-outline-tertiary"
            value={unitType === 'student'}
            onChange={() => setGroup({
              unitType: 'student',
              within,
            })}
          >
            {t('Personal')}
          </BtnToggle>
        </div>
        {isAdmin && (
          <div className="col">
            <BtnToggle
              className="btn-block btn-outline-tertiary"
              value={unitType === 'school'}
              onChange={() => setGroup({
                unitType: 'school',
                within: within === 'school' ? 'school' : within,
              })}
            >
              {t('School')}
            </BtnToggle>
          </div>
        )}
      </div>
      <div className="form-row my-3">
        {withins.filter(((w) => w.key !== unitType)).map((w) => (
          <div key={w.key} className="col">
            <BtnToggle
              className="btn-block btn-outline-tertiary"
              value={within === w.key}
              onChange={() => {
                if (w.key !== 'district') setDistrict('');

                setGroup({
                  within: w.key,
                  unitType,
                });
              }}
            >
              {t(w.label)}
            </BtnToggle>
          </div>
        ))}
      </div>
      <ListResource
        {...{
          searchable: searchableOptions,
          api: `${api}/${unitType}/${within}`,
          sort: defaultSort,
          filter,
          actions: {
            fileExport: true,
            find: false,
            create: false,
            remove: false,
          },
          cols: [
            // hide rank column when a specific period is selected and the
            // listing is a global 'all' view or a school listing. In these
            // cases the overall rank column is not meaningful for the
            // filtered period view.
            ...((period && (within === 'all' || unitType === 'school')) ? [] : [
              {
                key: 'rank',
                // disable overall rank sorting when a period filter is active
                sortable: !period,
                // backend now returns 1-based ranks; display as-is
                getter: (v) => (Number.isFinite(Number(v)) ? Number(v) : ''),
              },
            ]),
            {
              key: 'score',
              children: 'Total Score',
              // disable total score sorting when a period filter is active
              sortable: !period,
            },
            // hide orgLv column for school listings
            ...(unitType !== 'school' ? [{
              key: 'orgLv',
              children: 'Group',
              sortable: unitType !== 'school',
              getter: (v) => env.lvs[v]?.[lang] || '',
            }] : []),
            ...unitCols,
            // ...(env.user.roles.includes('admin') ? [
            //   {
            //     key: 'unit.school.title.zhhk',
            //     children: 'School',
            //     getter: (v, r) => getLang(get(r, 'unit.school.title'), lang),
            //   },
            //   {
            //     key: 'unit.school.district',
            //     children: 'District',
            //     getter: (v) => env.districts[v][lang],
            //   },
            // ] : []),
          ],
          ctrls: [
            <select
              className="custom-select mr-2"
              key="period"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
            >
              <option value="">{t('All Periods')}</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
            </select>,
            ...(within === 'district' ? [
              <select
                className="custom-select"
                key="district"
                onChange={(e) => setDistrict(e.target.value)}
              >
                <option value="">{t('Show All')}</option>
                {Object.keys(env.districts).map((d) => (
                  <option key={d} value={d}>
                    {env.districts[d][lang]}
                  </option>
                ))}
              </select>,
            ] : []),
          ],
        }}
      >
        {() => (
        <>
          {env.user.roles.includes('schoolAdmin') && (
            <ShowStudentRankingSwitch />
          )}
        </>
        )}
      </ListResource>
    </>
  );
};

export default ListRankings;
